import { apiGet, apiPost } from '@/services/api';
import type { Counts } from '@/features/publicsite/types/catalog/Counts';
import type { ServiceEvaluationPayload } from '@/features/publicsite/types/catalog/ServiceEvaluationPayload';
import type { SessionEnrolment } from '@/features/publicsite/types/catalog/SessionEnrolment';
import type { ProjectDiscussion } from '@/features/publicsite/types/community/ProjectDiscussion';
import type { ResearchAdoption } from '@/features/publicsite/types/research/ResearchAdoption';
import type { ServiceProvider } from '@/features/publicsite/types/services/ServiceProvider';
import type { ServiceRecord } from '@/features/publicsite/types/services/ServiceRecord';

const PUBLIC = '/api/v1/public';

/**
 * Every request the public catalogue pages (catalog/, CatalogCards) and the home page's sections
 * make — they and their hooks call these, never a URL.
 *
 * The `…Url` entries are addresses rather than fetchers because they are read through
 * usePublicList/usePublicData, which key their request on the address and the language: the
 * address is what changes when a search does (servicesUrl), and api.ts appends `lang` to it.
 */
export const publicCatalogApi = {
  /** One total per catalogue — the nine cards, the home hero and the closing figures. */
  counts: () => apiGet<Counts>(`${PUBLIC}/counts`),

  researchProjectsUrl: `${PUBLIC}/research/projects`,
  researchResultsUrl: `${PUBLIC}/research/results`,
  researchOrganizationsUrl: `${PUBLIC}/research/organizations`,
  /** One number per result, for the whole page — see ResearchProjectPage. */
  adoptionCountsUrl: `${PUBLIC}/research/adoption-counts`,
  adoptions: (resultId: string) =>
    apiGet<ResearchAdoption[]>(`${PUBLIC}/research/results/${resultId}/adoptions`),
  /**
   * A result's attached document. The endpoint, not a media URL: it is the only place that
   * re-checks the result is still published before handing the file over. A plain link.
   */
  resultDownloadUrl: (resultId: string) => `${PUBLIC}/research/results/${resultId}/download`,
  /** 204 when nobody has started one, which apiGet answers with undefined. */
  discussion: (projectId: string) =>
    apiGet<ProjectDiscussion | null>(`${PUBLIC}/research/projects/${projectId}/discussion`),
  /** Not a public endpoint: starting one takes a session. */
  startDiscussion: (projectId: string, message: string) =>
    apiPost<ProjectDiscussion>(`/api/v1/research/projects/${projectId}/discussion`, { message }),

  measuresUrl: `${PUBLIC}/funding`,
  programsUrl: `${PUBLIC}/funding/programs`,

  trainingsUrl: `${PUBLIC}/trainings`,
  /** The directory of PUBLISHED training institutions (§6.5). An address, as the others are. */
  trainingInstitutionsUrl: `${PUBLIC}/training/institutions`,
  /**
   * One institution with its portfolio. A fetcher rather than an address because the profile is
   * its own endpoint here — unlike the research organisation, whose list carries every field the
   * profile shows — and because a 404 (not PUBLISHED, or absent) has to reach the page.
   */
  trainingInstitutionUrl: (id: string) => `${PUBLIC}/training/institutions/${id}`,
  myEnrolments: () => apiGet<SessionEnrolment[]>('/api/v1/training/enrollments/me'),
  /** No body: the enrolment carries nothing but the session it is made in. */
  enrol: (sessionId: string) => apiPost(`/api/v1/training/sessions/${sessionId}/enrollments`),

  /** Unfiltered without `q=` at all, rather than with an empty one. */
  servicesUrl: (query: string) =>
    query ? `${PUBLIC}/services?q=${encodeURIComponent(query)}` : `${PUBLIC}/services`,
  /** Slug first, a bare id as a fallback — see ServiceProviderProfilePage. */
  provider: (idOrSlug: string) => apiGet<ServiceProvider>(`${PUBLIC}/providers/${idOrSlug}`),
  territorialUnitsUrl: `${PUBLIC}/territorial-units`,
  /** Unauthenticated: the record's own unguessable id is what the link trusts. */
  serviceRecord: (recordId: string) =>
    apiGet<ServiceRecord>(`${PUBLIC}/service-records/${recordId}`),
  evaluateServiceRecord: (recordId: string, body: ServiceEvaluationPayload) =>
    apiPost(`${PUBLIC}/service-records/${recordId}/evaluation`, body),

  forumsUrl: `${PUBLIC}/forums`,
  associationsUrl: `${PUBLIC}/associations`,
  qaUrl: `${PUBLIC}/qa`,

  /** The whole calendar, unfiltered — the home page shows its first three. */
  eventsUrl: `${PUBLIC}/events`,
};
